import React, { useState } from 'react';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Enquiry',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Thank you for your message. We\'ll get back to you within 24 hours.');
    setFormData({ name: '', email: '', phone: '', subject: 'General Enquiry', message: '' });
    setLoading(false);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      value: 'info@letwise.io',
      link: 'mailto:info@letwise.io',
      description: 'Send us an email anytime'
    },
    {
      icon: MessageCircle,
      title: 'Live Chat',
      value: 'Available 9am-5pm',
      description: 'Chat with our support team'
    },
    {
      icon: Clock,
      title: 'Response Time',
      value: 'Within 24 hours',
      description: 'We aim to respond quickly'
    }
  ];

  return (
    <PublicLayout>
      <SEO 
        title="Contact Us | LetWise"
        description="Get in touch with LetWise. Contact our team for enquiries about property management, landlord services, or tenant support."
      />
      
      <div className="bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="bg-primary text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
              <p className="text-xl opacity-90">
                We'd love to hear from you. Get in touch with our team.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Contact Methods */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {contactMethods.map((method, idx) => (
                <Card key={idx} className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary/10 rounded-xl flex items-center justify-center">
                    <method.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{method.title}</h3>
                  {method.link ? (
                    <a href={method.link} className="text-primary hover:underline font-medium">
                      {method.value}
                    </a>
                  ) : (
                    <p className="text-slate-900 font-medium">{method.value}</p>
                  )}
                  <p className="text-sm text-slate-500 mt-1">{method.description}</p>
                </Card>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="p-8">
                <h2 className="text-2xl font-bold mb-6">Send us a message</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                      <select
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="General Enquiry">General Enquiry</option>
                        <option value="Landlord Services">Landlord Services</option>
                        <option value="Tenant Support">Tenant Support</option>
                        <option value="Agent Partnership">Agent Partnership</option>
                        <option value="Contractor Registration">Contractor Registration</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Complaints">Complaints</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Message *</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="How can we help you?"
                      className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Card>

              {/* Company Info */}
              <div className="space-y-6">
                <Card className="p-8">
                  <h2 className="text-2xl font-bold mb-6">Our Office</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">C&C LET MANAGEMENT LIMITED</p>
                        <p className="text-slate-600">Trading as LetWise</p>
                        <p className="text-sm text-slate-500">Company No. SC869470</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">Registered Address</p>
                        <p className="text-slate-600">Unit 4, 416 Hamilton Road</p>
                        <p className="text-slate-600">Cambuslang, Glasgow</p>
                        <p className="text-slate-600">Scotland, G72 7XR</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">Email Addresses</p>
                        <p className="text-slate-600">General: <a href="mailto:info@letwise.io" className="text-primary hover:underline">info@letwise.io</a></p>
                        <p className="text-slate-600">Support: <a href="mailto:support@letwise.io" className="text-primary hover:underline">support@letwise.io</a></p>
                        <p className="text-slate-600">Complaints: <a href="mailto:complaints@letwise.io" className="text-primary hover:underline">complaints@letwise.io</a></p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-primary/5 border-primary/20">
                  <h3 className="font-semibold mb-2">Property Redress Scheme</h3>
                  <p className="text-sm text-slate-600 mb-2">
                    We are a member of the Property Redress Scheme providing independent complaints resolution.
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Membership:</span>{' '}
                    <span className="font-mono">PRS056298</span>
                  </p>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-2">Opening Hours</h3>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p className="flex justify-between"><span>Monday - Friday</span><span className="font-medium">9:00am - 5:00pm</span></p>
                    <p className="flex justify-between"><span>Saturday</span><span className="font-medium">10:00am - 2:00pm</span></p>
                    <p className="flex justify-between"><span>Sunday</span><span className="font-medium">Closed</span></p>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Emergency maintenance support is available 24/7 for managed properties.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
